import {
  assertEvent,
  assign,
  fromPromise,
  sendTo,
  setup,
  type SnapshotFrom,
} from 'xstate';

import {bookingMachine} from '../../machines/bookingMachine';
import {identificationMachine} from '../../machines/identificationMachine';
import {rsvpMachine} from '../../machines/rsvpMachine';
import {
  cancelEvent,
  getEvent,
  getEventTemplate,
  getReasons,
  getSession,
  rescheduleRequest,
} from '../../services/schedularService';
import type {IParticipantType} from '../../types/IParticipantType';
import {getShortSha256Hash} from '../../utils/crypto';
import {isTimezoneSupported} from '../../utils/booking';

import type {
  IRootMachineContext,
  IRootMachineEvents,
  IRootMachineInput,
} from './rootMachine.types';
import {getRsvpStatus} from '../../utils/common';
import {isBrowserSupported} from '../../utils/browser';

export const rootMachine = setup({
  types: {
    context: {} as IRootMachineContext,
    events: {} as IRootMachineEvents,
    input: {} as IRootMachineInput,
  },
  actors: {
    getEvent: fromPromise(async ({input}: {input: {eventId: string}}) =>
      getEvent(input.eventId),
    ),
    getSession: fromPromise(async ({input}: {input: {sessionId: string}}) =>
      getSession(input.sessionId),
    ),
    getTemplateBySuffix: fromPromise(
      async ({input}: {input: {agentId: string; suffix: string}}) =>
        getEventTemplate(input.agentId, input.suffix),
    ),
    getReasons: fromPromise(async () => getReasons()),
    getOwner: fromPromise(
      async ({
        input,
      }: {
        input: {eventId: string; attendee?: string};
      }): Promise<IParticipantType | undefined> => {
        const guestHash = await getShortSha256Hash(`guest_${input.eventId}`);
        if (guestHash === input.attendee) {
          return 'CANDIDATE';
        }
        const hostHash = await getShortSha256Hash(`host_${input.eventId}`);
        if (hostHash === input.attendee) {
          return 'HOST';
        }
        const interviewerHash = await getShortSha256Hash(
          `interviewer_${input.eventId}`,
        );
        if (interviewerHash === input.attendee) {
          return 'INTERVIEWER';
        }
        return undefined;
      },
    ),
    cancelEvent: fromPromise(
      async ({
        input,
      }: {
        input: {
          eventId: string;
          reason: string;
          viewerTimezone: string;
          attendee: string;
          aid: string;
        };
      }) =>
        cancelEvent(input.eventId, {
          viewer_timezone: input.viewerTimezone,
          attendee: input.attendee,
          reason: input.reason,
          aid: input.aid,
        }),
    ),
    sendRescheduleRequest: fromPromise(
      async ({
        input,
      }: {
        input: {eventId: string; reason: string; aid: string};
      }) =>
        rescheduleRequest(input.eventId, {
          reason: input.reason,
          aid: input.aid,
        }),
    ),
    generateAttendee: fromPromise(async ({input}: {input: {eventId: string}}) =>
      getShortSha256Hash(`guest_${input.eventId}`),
    ),
    rsvpMachine,
    bookingMachine,
    identificationMachine,
  },
  guards: {
    isBrowserNotSupported: () => !isBrowserSupported(),
    isRescheduleLimitReached: ({context}) => {
      return context.eventDetails?.reschedule_attempts_left === 0;
    },
    isEditBooking: ({context}) => {
      if (!context.currentRoute) {
        return false;
      }
      return ['cancel', 'reschedule', 'rsvp'].includes(context.currentRoute);
    },
    isSession: ({context}) => !!context.sessionId,
    isCancellation: ({context}) => context.currentRoute === 'cancel',
    isReschedule: ({context}) => context.currentRoute === 'reschedule',
    isRsvp: ({context}) => context.currentRoute === 'rsvp',
    isCancellationNoticePass: ({context}) =>
      !!context?.eventDetails?.did_cancellation_notice_pass,
    isAttendeeAvailable: ({context}) => !!context.attendee,
    isHostOrInterviewer: ({context}) =>
      context.owner === 'HOST' || context.owner === 'INTERVIEWER',
    isCandidate: ({context}) => context.owner === 'CANDIDATE',
    isSessionExpired: ({context}) => !!context?.session?.is_expired,
    isTimezoneSupported: ({context}) =>
      context.timezone ? isTimezoneSupported(context.timezone) : false,
    isBookingSuccess({event}) {
      assertEvent(event, 'xstate.done.actor.bookingFlow');
      return event.output.finalState === 'BookingSuccess';
    },
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QCcD2qAuBiANgQwDsoBXPGAOmIAcI8MwBtABgF1FQrVYBLDb1AuxAAPRAEYAHADYAzOSYBOAExMALBLEB2KWMkKANCACeiALSbNE+duVKlszQFYmEgL6vDaTOVgY8ybGY2JBBOHj4BIVEEUzEZdXIZBRlHCxkpKQks1UMTGMcZOXUlVQySnSltd090DB8-AKwGMWCOLl5+QRDo0wlHJXJHZyYZCUVpKQUJXLMCoolyqXKxSqlqkC86339ApVbQ9oiu0GiV8lUlZQ0rqbixGfylKwvNC4VNJk-Stw8N2vI+ABbMAALwEYAAMqg8BBIE1WEIwh1It0zNpnk5rjIVBIFP0HqYUkx5GIFKoZDYxKpkqp1ptyMgwHhYAJYFCYdwiFgIODyJyAG6oADWYAZ-0ZzNZ7IgnKgCAFqAAxnROkEgojDp0orMqfIZGoJNiXJ81AT0mJBkptBSlDIxEN4mtfvSJSyCGzoTKuTyCKKFSKxd5XVLPbL5QRBcqjmqWhrwlrUfldSMDUaxl8CapFJbrZpbfaCqU6eKmW6PRzvbz-aKXaWQxW5Qqo6rWAw9nHkccRDrVHrUziTTljGZVKkcw58w6i86S5L3dLZVgwMg0MhyFR8BgAGaoZCAwN1YPz0NEcORlUCNUIkJIo7apO9lPqNODgnORzjm12qdOmpBuvugAoiuu5YIyGDIEY6o3pqKInLM-TkJoFJMJUmgKAoujTMOMR2nI-S5pOha-n83hAqC4IAArcIqIrIFg5Fgr6PhgBg0FtPGcHdrhTjkFIpQFFIny2hc9iZh8gzoQsTyGnYhrFt4YD8mABAYAulbMdWB7kEpKlqSejYRkqF4EFe+y3gm8GPAMlTUnmujvAs9w4aYDqfnm36FgpdS6ap6lQEuIFrhudA7nu2m+fpDZnsZ0atteHGdverl2HxrzvEoDmWJlBJCRaBETp58TeSxsA8AI-nclWRkBvSsBwOVBD+TFzaXvF5mwV20TOBaWgpgo4xMHojgPBSVh2GoYhMK8ihPCV9VlZ0lXLqu66bmF+51Q1S0GS1JlmR2d6JlNlyJIU9hSI4mFWiNOEqB+2JJKkeaYdSjglZFAAqYCAiF9CVT6fo1TW-xfT9f2QrtTb7e1h2WdxKxkoM5rCaoVJOJoDyyfIV0UpMeapJdH3Kap32-ZukMNoFq1-RtEUkxgZMQ810NxSw7EHJxXWIJljhSIkVJKI4qjksLihSA8l29hhmV2FaZKSO9M7eLC9B7pyYAAPIAO6+vRgN8sD2mq8ugIazret7WzHMWVxPSOLogwjJYjgSNS6QXbl1LuURxXKz5DP+cBq5gaxkE251yWjpo8i4pYyTTToo4PIL5BiELTBXdSkiZW783bRVnpwhHXP3uoViVEwlz2GjVfoZLlhIZIl3p9oIuFPni2FzCxexjBpfHeSvb6unruZJnFKS4a8iKI6pLC5oWid41wegeB4cJZzSWJoaH4WIoQwqOkyjOXkqej5nZKYU5CglQARugQqygAYjgqDa1VmlGw-wov2-2sl23lZFYCQML6ndpMNGZIU7pwtKoD4txcQZAkK8e+j8-7v0-kDQUAYf5PyIK-d+zQOoDysiJAYi90pxGkOhJQMCzjxAKHYOI6ERZoN-gQ-+uBCAkDIKKagtB6CAKOmQvqfFpCMKSJkFIMgsYXDTmSHqQ9UJWmJnpJmFNV70XXlBTettuYICPnITOnxhbJGUOSOhOFhYWhxIUTIU10j9BKu-PW0pi56Mjomcw7xzhCQWPqKu2I4gp2dokK6qF+JxGmgsFxutlzuIgE0PuiURHcUdOQJ4YsFiZ2FjdUJCRXYGlKMhVI004luKLkktsJCgEIySAMcWKDLh8xKEMApVginqBKSkD4mh2H4KgAAZWIIqRUDUsGGxwSDbweDZQjLGQ1K2LZ2aeNIQjReFCrTnSeELakViz4WAUPIC48R4HSJ0D8P8dRlQEHGTgTcnRCHa0NrweEtS0k9CpHiJCN1JDkiyIolOI95BCWKLaBQGQUglVufcx5AhnmvMCCkrenyzCwOHiU143xpr2mBUNUFBo7BSNkEra55BYVgAeSZRFnI3k1LhnbMwYwBhEhyULPM4x8UWlQkSiFUKyWkRuYQOFNL-4MgAs-Xc+4NqhznFK8KsBiB3zNmxNZdSvmSBjv0eWFhMRUlumfV2VgXBjSSHaD4sT-YUpFVS+FBBEVHgVfuJVKreB8A0tg4UMzhV3LtWK9+Er5XSp8Mq1VHrDLnmtuqtFMQsLEmUIoD4Zz+KmhwvPfmKFFB4lspkfp1rKXUqOI6yVIbXXhsXCtXca1QohvpIW+1Jbg2KrDe6sMrMVnCPhj0dC-N06KDRikNGeUU46GOVXPEcQhYLDxDIGFtqi1PPFU6kNVbtGlpbW6tVHzu1mGSEUGSMl1B81SCnIY+UnroUKJhZIJFaywEVAAC0gMQHAYBEU4G4KqgAcpgAASkyJ9Hid1MsMVaNOkgR7IPTk4lO5JjnZUTRSeILhaTWsZA+59EBX3vvFZ+n9-7ANYeSSBgxQwrBxCzCUPEQwBqyPTcO8gYxSVo3UANW0JUMNAew2+j9X7eC-owABvA3Gmjtn7hqxAYw5B43g-YSFVIhxn3g0hGdR9kJZjzuhuA3GcNNrdM68gsqV2btVV20DrkrApEug7IYrwrpC0NeIa4TGsjaH6FMSwHdtOYZfbx5dG6XWtowBGyZWl726f84GkzQWt0RuWW1VZpH7zC3wgJKd1xNmjsmJk+wclZBxAyJxnTWG9MBebbFitXI101u3HWksvmeO4ei4F0NcX21GVaqZWGEnY2pcGOl7EmX07ZeOXYJYTwCvpDvQ1yLzWXkxZ0kFOVBmy3BfMwYi4vZbQETYwsISqEU6UNyxNiRhWZv-ka2VwNczOGYINlpW7UBnkbfvM3OBWRRguDiHiODUwmOfETbq3EVyhUSqu1Fl5T3nlhe-ugu7ACUX6PvLaEY5xULfkKFkFYZ7MpIQsFAlwJQp3FYh-N8g0OuH4CIKQCgAi6CMBjbuhAU1vYJyyNINQVosijuzENDCo57BUimqDiLpXIdIveYygxrlh7fiEpcT4uJprAqmqpqYTwqQ6AdKTubtKCD0qR14shdoIN2kF63JzLOLXnGSIpvpUkBmygAMILudVgJ7ru-U4EM+W3gr3ExJGJMLq6vbtmkgeJlRuDl4gYUsMhfN5LPcLpGe1z1UzvXaWT971PVXI2xU7UzizyvEj8QKN8uYk87oqAGFdVImv1MXbqNn+5ue23VaCrVum9IW9UrbyFjrUbC-Je8bic4WZUiOVHCMckkfs18RcNkCkThMJO6IF7+5WiPfw6gBvqlvv1tF5l6OC0hoomAuYSNu6ag5BuyWJkUomdkKi-+HQegBBYRgAAOIqWXAzpJXG4uooUAqAAewCwsmaVIFI5Isgdg8CKcFiTGNI1mNC8CaG5Kb+Kkn+P+es-+WADaJk5AIBYBCM56iQIwsg8QTk2IVuE0PKeIfQgKwSIw7gvwBAqAsI8AIQmw0uyUiMxIowR89oWQ06Vu5gFI8gVolclG6EQw80DQGAvB3i9okK5AkKVcmc9oQS8BLkBQxIaEokMGfUYgJUxA7o1AnAAQkAAAQmgNrPVMgEoVZLEPEHIFoH0A7NoAgigpmASorrRrPJUGMOgWDoxOCIkk4dxL0I7Kzs-i7JNJjC5CLBaJCm7MwhMNfMVnOOWF6FAJET0BoDHNNhcNNMaBmC5JIHAhlANCLLUeclkWWFovkWYMhgoqUg0nXuoIkXkISHEDmPHuhCokJE3gCF+hRL6NRLRMuM0Szo0ksIoENG7JhC4JmPAgvtiGUJUEkK9Gon5AZDMb0C4HxLZnPI5DlNfjJqYnzIvBPHOtagtI1P5DMeoL1HmI-kMPsighLOmtPMOroPaOdEkCEfSGDOTAzk8b1szojDymmChg7H0AYDhGUExlmANITJ8OnCMSbOrL6BbNMZCcXmjvJrXEsR7N0YgP0HIMSrvFMLiJlLfNapFEHEFAcY5khKMPqM0uiPaN8cpgMBfFnNfLnMCf8A8TtD3BAM8RhGnEJMhCsLoBYLyRSVoEhKahChyVNInmDmKQIE0QSQYo4jZDOjya8EsOoHBvyRnIKTnP8mvs9v-DMSsHmGnA7GSNNH0nlg8FfOEkLhoK7CUKOLsYzODJoiyfqSjloMYkNJkLINnBkA8BAb8svikBoPBgyeSq4gklUgcRYFshNksLRq7FbighaJdPHChO5nmMvJ0IBMIFQNwIyJKeGYmF0ZkpUFMKlshHYE4A8C7GdOnCLHMOhOhHaQsuMmVI6ZsgDqcgNMscLNhGfKSpksoPOZMAasoCVOwXwFuEYGOQ1DMULPEANtridJhHaCnIwYkATphFoKkHzCKd4AQcWg6c2c4SAqyjkt+CIUWSnJlBaHaJnPmIvHZC-o+Quo2uKnSooa+VESocPJ+cIQsD+empcFYBoLkrKWgfOt7hBYGk+Z0JRKgJ+oqHkKkszrEKOJ0vxMhnaGAgueIJYnxMvqSBsbJNhaKs+S1hVgcTBh+DqsORYK7AanBtiOcM4G9K7IUE4CMfhQiuVqtuFBtDxfKa5sUoOahUqSzq6Woe+E8OoBkFoFqfWuBQGgtq1n7gPkQMpRkKXtiBcHSRkJnKOiLJJILEUgsKBb6hxUulxQpfuGugcZUB+MoKMOnLzHAZpQWBeniFekkCxTJSZZxS8twLCKpNwFuDRKZQcXXsuT1ENHoNHKrsSGgQ7KMHlGCrrkAc8qyWsSUPZGLCoVQb+enOcPqP6RhBhKhA+YeCVn5uTvhgJoRiJlhgealDYEPNfNPuSdbtoExq7F4YvFfGwj5nrvJQitKjxdKXVbzFmI1eaemkJb8mOvXhoCUJVX1fputYpRtTBV8g7JkvAjtUNLjPtWfG7G4QCbLLQlnOdU1pdQ6mtmnnkbdeIDoNOTQZYKSJsgcs5kLE7MglfKhJCoKmLhdWtQDeFAFSDQgAVpkiLueiLBoLoEdiqeNikJ4YZUZbNlVeKpTu-DMUsHIF5igjehcA-meikGoUTdIGxmYt1eDqtYGilXpOlZlUlY6WoL2ArvxO5tSCMDDSzi8IkGkZYKOE9HYL9ddslQbtBWRRZloBaBpnKXzNiKhUpuIJcMcgnNSGzc4JcPzYAWjS1mThCPxkJkRpAAceSMcXoPqPHiLIiWfEkFZnLJQlNNQlTUGLAPyFQNVdjVSNNGoXZKbdSANK8M1VRVmDGZPoaPzb3j7jdXrQaaYoMAUO8K8JQizZHioPQakCLOHtOEnjvnvjgP3hGoFfdRavxAsC9GSPRYYoZYMDmvzvxAwfzYWnuROdjbEFqkndILoF+QrJHqSMcm7ITCkJbS4HaS3XqUXXwSoIMCWbaMaakCkHPt7XfvYHfk-ukCVABgAI7EBwDu1k6T1cF72Jj7qZIoSCEr1kgK2SBjBXnGq2hLAwaeXkCYEf5gDf6-7ID-6TljVYSFbXSzwIElBIGuF8yoGoKsFAA */
  context: ({input}) => ({
    id: input.id,
    currentRoute: input.currentRoute,
    attendee: input.attendee,
    aid: input.aid,
    timezone: input.timezone,
    sessionId: input.sessionId,
    language: input.language,
    countyCode: input.countyCode,
  }),
  id: 'root',
  initial: 'start',
  on: {
    'language.update': {
      actions: assign({
        language: ({event}) => event.language,
      }),
    },
  },
  states: {
    start: {
      always: [
        {
          target: 'unsupportedBrowser',
          guard: {
            type: 'isBrowserNotSupported',
          },
        },
        {
          target: 'timezoneLoaded',
          guard: {
            type: 'isTimezoneSupported',
          },
        },
        {
          target: 'timezonePicker',
        },
      ],
    },

    unsupportedBrowser: {
      type: 'final',
    },

    timezoneLoaded: {
      always: [
        {
          target: 'reasonsLoading',
        },
      ],
    },

    reasonsLoading: {
      invoke: {
        id: 'rootNew.reasonsLoading:invocation[0]',
        input: () => ({}),
        onDone: [
          {
            target: 'eventLoading',
            guard: {
              type: 'isEditBooking',
            },
            actions: assign({
              reasons: ({event}) => event.output,
            }),
          },
          {
            target: 'sessionLoading',
            guard: {
              type: 'isSession',
            },
            actions: assign({
              reasons: ({event}) => event.output,
            }),
          },
          {
            target: 'eventTemplateLoading',
            actions: assign({
              reasons: ({event}) => event.output,
            }),
          },
        ],
        onError: {
          target: 'reasonsError',
          actions: assign({error: ({event}) => event.error}),
        },
        src: 'getReasons',
      },
    },

    reasonsError: {
      on: {
        retry: {
          target: 'reasonsLoading',
        },
      },
    },

    timezonePicker: {
      on: {
        'timezone.set': {
          target: 'timezoneLoaded',
          actions: assign({
            timezone: ({event}) => event.timezone,
          }),
        },
      },
    },

    eventLoading: {
      invoke: {
        id: 'rootNew.eventLoading:invocation[0]',
        input: ({context: {id}}) => ({eventId: id}),
        onDone: {
          target: 'determineOwner',
          actions: assign({
            eventDetails: ({event}) => event.output,
          }),
        },
        onError: {
          target: 'eventLoadingError',
          actions: assign({error: ({event}) => event.error}),
        },
        src: 'getEvent',
      },
    },

    sessionLoading: {
      invoke: {
        input: ({context}) => {
          if (context.sessionId) {
            return {
              sessionId: context.sessionId,
            };
          }
          throw new Error('sessionId not found');
        },
        onDone: {
          target: 'sessionLoaded',
          actions: assign({
            session: ({event}) => event.output,
          }),
        },
        onError: {
          target: 'sessionError',
          actions: assign({error: ({event}) => event.error}),
        },
        src: 'getSession',
      },
    },

    eventTemplateLoading: {
      invoke: {
        input: ({context: {currentRoute, id}}) => ({
          agentId: currentRoute,
          suffix: id,
        }),
        onDone: {
          target: 'bookingFlow',
          actions: assign({
            eventTemplate: ({event}) => event.output,
          }),
        },
        onError: {
          target: 'eventTemplateError',
          actions: assign({error: ({event}) => event.error}),
        },
        src: 'getTemplateBySuffix',
      },
    },

    determineOwner: {
      invoke: {
        id: 'rootNew.determineOwner:invocation[0]',
        input: ({context}) => {
          if (context.eventDetails?.id) {
            return {
              eventId: context?.eventDetails?.id,
              attendee: context?.attendee,
            };
          }
          throw new Error('eventId not found');
        },
        onDone: {
          target: 'ownerLoaded',
          actions: assign({
            owner: ({event}) => event.output,
          }),
        },
        src: 'getOwner',
      },
    },

    eventLoadingError: {
      on: {
        retry: {
          target: 'eventLoading',
        },
      },
    },

    sessionLoaded: {
      always: [
        {
          target: 'sessionExpired',
          guard: {
            type: 'isSessionExpired',
          },
        },
        {
          target: 'eventTemplateLoading',
        },
      ],
      entry: assign({
        language: ({context}) =>
          context.session?.guest_language || context.language,
      }),
    },

    sessionError: {
      on: {
        retry: {
          target: 'sessionLoading',
        },
      },
    },

    bookingFlow: {
      on: {
        'language.update': {
          actions: [
            assign({
              language: ({event}) => event.language,
            }),
            sendTo(
              ({system}) => system.get('bookingMachine')!,
              ({event}) => ({
                type: 'language.update',
                language: event.language,
              }),
            ),
          ],
        },
      },
      invoke: {
        id: 'bookingFlow',
        input: ({context: {timezone, eventTemplate, language, session}}) => {
          if (timezone) {
            return {
              timezone,
              eventTemplate,
              language,
              session,
            };
          }
          throw new Error('timezone not found');
        },
        src: 'bookingMachine',
        systemId: 'bookingMachine',
        onDone: [
          {
            target: 'bookingSuccess',
            actions: assign({
              eventDetails: ({event}) => event.output.eventDetails,
            }),
            guard: {
              type: 'isBookingSuccess',
            },
          },
          {
            target: 'notifySuccess',
          },
        ],
      },
    },

    eventTemplateError: {
      on: {
        retry: {
          target: 'eventTemplateLoading',
        },
      },
    },

    ownerLoaded: {
      always: [
        {
          target: 'cancellationFlow',
          guard: {
            type: 'isCancellation',
          },
        },
        {
          target: 'rescheduleFlow',
          guard: {
            type: 'isReschedule',
          },
        },
        {
          target: 'rsvpFlow',
          guard: {
            type: 'isRsvp',
          },
        },
      ],
      entry: assign({
        language: ({context}) => {
          const users =
            context.owner === 'HOST' || context.owner === 'INTERVIEWER'
              ? context?.eventDetails?.interviewers
              : context?.eventDetails?.candidates;

          const currentUser = users?.find((user) => user.id === context.aid);
          return currentUser?.language || context.language;
        },
      }),
    },

    sessionExpired: {
      type: 'final',
    },

    bookingSuccess: {
      invoke: {
        input: ({context: {eventDetails}}) => ({
          eventId: eventDetails?.id || '',
        }),
        src: 'generateAttendee',
        onDone: {
          target: 'attendeeGenerated',
          actions: assign({
            attendee: ({event}) => event.output,
            owner: 'CANDIDATE',
          }),
        },
      },
    },

    notifySuccess: {
      type: 'final',
    },

    cancellationFlow: {
      initial: 'init',
      states: {
        init: {
          always: [
            {
              target: 'cancellationPolicy',
              guard: {
                type: 'isCancellationNoticePass',
              },
            },
            {
              target: 'reasonForm',
              guard: {
                type: 'isAttendeeAvailable',
              },
            },
            {
              target: 'identificationFlow',
            },
          ],
        },
        cancellationPolicy: {
          type: 'final',
        },
        reasonForm: {
          initial: 'form',
          states: {
            form: {
              on: {
                'reasonForm.submit': {
                  target: 'submitting',
                },
              },
            },
            submitting: {
              entry: assign({
                reason: ({event}) => {
                  assertEvent(event, 'reasonForm.submit');
                  return event.reason;
                },
              }),
              invoke: {
                input: ({event, context}) => {
                  assertEvent(event, 'reasonForm.submit');
                  if (context.eventDetails?.id) {
                    return {
                      eventId: context.eventDetails?.id,
                      reason: event.reason,
                      viewerTimezone:
                        context.eventDetails?.primary_guest_timezone || '',
                      attendee: context.attendee || '',
                      aid:
                        context.eventDetails?.candidates?.[0]?.id ||
                        context.aid ||
                        '',
                    };
                  }
                  throw new Error('eventId not found');
                },
                onDone: {
                  target: '#root.cancelSuccess',
                },
                onError: {
                  target: 'error',
                  actions: assign({
                    error: ({event}) => event.error,
                  }),
                },
                src: 'cancelEvent',
              },
            },
            error: {
              on: {
                'reasonForm.submit': {
                  target: 'submitting',
                },
              },
            },
          },
        },
        identificationFlow: {
          invoke: {
            id: 'rootNew.cancellationFlow.identificationFlow:invocation[0]',
            input: ({context: {eventDetails}}) => {
              if (eventDetails?.id) {
                return {
                  eventId: eventDetails?.id,
                  linkType: 'cancel',
                };
              }
              throw new Error('eventId not found');
            },
            src: 'identificationMachine',
            systemId: 'identificationMachine',
          },
        },
      },
    },

    rescheduleFlow: {
      initial: 'init',
      states: {
        limitNotReached: {
          always: [
            {
              target: 'reasonForm',
              guard: {
                type: 'isHostOrInterviewer',
              },
            },
            {
              target: 'bookingFlow',
              guard: {
                type: 'isCandidate',
              },
              actions: assign({
                isReschedule: true,
              }),
            },
            {
              target: 'identificationFlow',
            },
          ],
        },
        reasonForm: {
          initial: 'form',
          states: {
            form: {
              on: {
                'reasonForm.submit': {
                  target: 'submitting',
                  actions: assign({
                    reason: ({event}) => event.reason,
                  }),
                },
              },
            },

            submitting: {
              invoke: {
                id: 'rootNew.rescheduleFlow.rescheduleRequest.submitting:invocation[0]',
                input: ({event, context}) => {
                  assertEvent(event, 'reasonForm.submit');
                  if (context.eventDetails?.id && context.aid) {
                    return {
                      eventId: context.eventDetails.id,
                      reason: event.reason || '',
                      aid: context.aid || '',
                    };
                  }
                  throw new Error('eventId or aid not found');
                },
                onDone: {
                  target: '#root.RequestRescheduleSuccess',
                  reenter: true,
                },
                onError: {
                  target: 'error',
                  actions: assign({error: ({event}) => event.error}),
                },
                src: 'sendRescheduleRequest',
              },
            },

            error: {
              on: {
                'reasonForm.submit': {
                  target: 'submitting',
                },
              },
            },
          },
        },
        bookingFlow: {
          on: {
            'language.update': {
              actions: [
                assign({
                  language: ({event}) => event.language,
                }),
                sendTo(
                  ({system}) => system.get('bookingFlow'),
                  ({event}) => ({
                    type: 'language.update',
                    language: event.language,
                  }),
                ),
              ],
            },
          },
          invoke: {
            id: 'bookingFlow',
            input: ({
              context: {timezone, eventDetails, attendee, language},
            }) => {
              if (timezone) {
                return {
                  timezone,
                  eventDetails,
                  attendee,
                  language,
                };
              }
              throw new Error('timezone not found');
            },
            onDone: [
              {
                target: '#root.bookingSuccess',
                actions: assign({
                  eventDetails: ({event}) => event.output.eventDetails,
                  reason: ({event}) => event.output.rescheduleReason,
                }),
                guard: {
                  type: 'isBookingSuccess',
                },
              },
              {
                target: '#root.notifySuccess',
              },
            ],
            src: 'bookingMachine',
            systemId: 'bookingMachine',
          },
        },
        identificationFlow: {
          invoke: {
            id: 'rootNew.rescheduleFlow.identificationFlow:invocation[0]',
            input: ({context: {eventDetails}}) => {
              if (eventDetails?.id) {
                return {
                  eventId: eventDetails?.id,
                  linkType: 'reschedule',
                };
              }
              throw new Error('eventId not found');
            },
            src: 'identificationMachine',
            systemId: 'identificationMachine',
          },
        },
        init: {
          always: [
            {
              target: 'rescheduleLimitReached',
              guard: 'isRescheduleLimitReached',
            },
            {
              target: 'limitNotReached',
              reenter: true,
            },
          ],
        },

        rescheduleLimitReached: {},
      },
    },

    rsvpFlow: {
      invoke: {
        id: 'rootNew.rsvpFlow:invocation[0]',
        input: ({context}) => {
          const {eventDetails, aid} = context;
          const rsvpStatus = getRsvpStatus(eventDetails, aid, context.owner);
          if (aid && eventDetails?.id) {
            return {
              eventId: eventDetails?.id,
              aid,
              rsvpStatus,
            };
          }
          throw new Error('aid and eventId not found');
        },
        src: 'rsvpMachine',
        systemId: 'rsvpMachine',
      },
    },

    bookingCancelForm: {
      on: {
        'bookingCancelForm.submit': {
          target: 'bookingCancelSubmitting',
          actions: assign({
            reason: ({event}) => event.reason,
          }),
        },
      },
    },

    bookingCancelSubmitting: {
      invoke: {
        input: ({event, context}) => {
          assertEvent(event, 'bookingCancelForm.submit');
          if (context.eventDetails?.id) {
            return {
              eventId: context.eventDetails?.id,
              reason: event.reason,
              viewerTimezone:
                context.eventDetails?.primary_guest_timezone || '',
              attendee: context.attendee || '',
              aid:
                context.eventDetails?.candidates?.[0]?.id || context.aid || '',
            };
          }
          throw new Error('eventId not found');
        },
        onDone: {
          target: 'cancelSuccess',
        },
        onError: {
          target: 'bookingCancelError',
          actions: assign({error: ({event}) => event.error}),
        },
        src: 'cancelEvent',
      },
    },

    cancelSuccess: {
      type: 'final',
    },

    bookingCancelError: {
      on: {
        'bookingCancelForm.submit': {
          target: 'bookingCancelSubmitting',
        },
      },
    },

    RequestRescheduleSuccess: {
      type: 'final',
    },

    attendeeGenerated: {
      on: {
        'reschedule.go': {
          target: 'rescheduleFlow',
        },
        'cancellation.go': {
          target: 'bookingCancelForm',
        },
      },
    },
  },
});

export type IRootMachineSnapshot = SnapshotFrom<typeof rootMachine>;
